import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { api, createIdempotencyKey, isPreviewMode } from "../api/client";
import type { AccountListCreateResult, ImportPreview, ImportPreviewItem } from "../api/types";
import { formatDate } from "../utils/format";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

interface ImportWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: AccountListCreateResult, startTask: boolean) => void;
}

interface LocalInspection {
  nonEmpty: number;
  invalid: number;
  bytes: number;
}

const steps = ["提供数据", "逐行校验", "保存批次"];
const MAX_IMPORT_BYTES = 2_097_152;

function defaultListName() {
  const date = new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit" }).format(new Date());
  return `${date.replace("/", "-")} OAuth 批次`;
}

function inspectLocally(jsonl: string): LocalInspection {
  let nonEmpty = 0;
  let invalid = 0;
  for (const line of jsonl.split(/\r?\n/)) {
    const value = line.trim();
    if (!value) continue;
    nonEmpty += 1;
    try {
      const parsed: unknown = JSON.parse(value);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") invalid += 1;
    } catch {
      invalid += 1;
    }
  }
  return { nonEmpty, invalid, bytes: new Blob([jsonl]).size };
}

function repairHint(messages: string[]) {
  const text = messages.join(" ").toLowerCase();
  if (text.includes("invalid json") || text.includes("expecting") || text.includes("json value")) return "检查这一行的双引号、逗号和末尾括号；每行只能有一个 JSON 对象。";
  if (text.includes("mailapi") || text.includes("mailbox")) return "补充可访问的 mailapi_url，或检查 mailbox.mailapi_url 的字段位置。";
  if (text.includes("email") || text.includes("identity")) return "补充 email 或 login_identity，值不能留空。";
  if (text.includes("duplicate")) return "此邮箱在本批次重复；保留需要执行的一行即可。";
  if (text.includes("too large") || text.includes("max lines")) return "拆成多个较小批次后分别导入。";
  return "返回编辑器检查该行字段，修改后重新执行服务端校验。";
}

function PreviewRow({ item, onFix }: { item: ImportPreviewItem; onFix: (line: number) => void }) {
  const messages = [...item.errors, ...item.warnings];
  return (
    <article className={`import-preview-row${item.ok ? " is-valid" : " is-invalid"}`}>
      <span className="import-preview-row__line">L{item.line_no}</span>
      <span className="import-preview-row__state"><Icon name={item.ok ? "check" : "alert"} size={16} />{item.ok ? "可导入" : "需修复"}</span>
      <div className="import-preview-row__identity"><b>{item.email || item.login_identity || "未识别账号"}</b><small>{item.mailapi_url || "未显示邮箱接口"}</small></div>
      <div className="import-preview-row__messages">
        {messages.length ? messages.map((message, index) => <span key={`${message}-${index}`}>{message}</span>) : <span>字段结构通过服务端校验</span>}
        {!item.ok ? <small>{repairHint(messages)}</small> : null}
      </div>
      {!item.ok ? <button type="button" className="button button--quiet button--small" onClick={() => onFix(item.line_no)}>定位此行</button> : null}
    </article>
  );
}

export function ImportWizard({ open, onClose, onCreated }: ImportWizardProps) {
  const { pushToast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [jsonl, setJsonl] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [listName, setListName] = useState(defaultListName);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [created, setCreated] = useState<AccountListCreateResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [preflightKey, setPreflightKey] = useState("");
  const [saveKey, setSaveKey] = useState("");
  const inspection = inspectLocally(jsonl);

  function reset() {
    setStep(0);
    setJsonl("");
    setSourceName("");
    setListName(defaultListName());
    setPreview(null);
    setCreated(null);
    setBusy(false);
    setDragging(false);
    setError("");
    setDiscardOpen(false);
    setPreflightKey("");
    setSaveKey("");
  }

  function closeNow() {
    reset();
    onClose();
  }

  function requestClose() {
    if (busy) return;
    if (jsonl.trim() && !created) {
      setDiscardOpen(true);
      return;
    }
    closeNow();
  }

  async function loadFile(file?: File) {
    if (!file) return;
    setError("");
    if (file.size > MAX_IMPORT_BYTES) {
      setError(`文件超过服务端 2 MiB 上限（当前 ${file.size.toLocaleString("zh-CN")} 字节），请拆分后再导入。`);
      return;
    }
    try {
      const text = await file.text();
      setJsonl(text);
      setSourceName(file.name);
      setPreview(null);
      setPreflightKey("");
      setSaveKey("");
      setStep(0);
      window.setTimeout(() => textareaRef.current?.focus(), 0);
    } catch {
      setError("文件读取失败，请重新选择文件或直接粘贴内容。");
    }
  }

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    void loadFile(event.target.files?.[0]);
    event.target.value = "";
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    void loadFile(event.dataTransfer.files?.[0]);
  }

  async function runPreflight() {
    if (!jsonl.trim()) {
      setError("请先粘贴 JSONL，或从本机选择文件。");
      textareaRef.current?.focus();
      return;
    }
    if (inspection.bytes > MAX_IMPORT_BYTES) {
      setError(`内容超过服务端 2 MiB 上限（当前 ${inspection.bytes.toLocaleString("zh-CN")} 字节），请拆分批次。`);
      return;
    }
    const key = preflightKey || createIdempotencyKey("import-preflight");
    if (!preflightKey) setPreflightKey(key);
    setBusy(true);
    setError("");
    try {
      const result = await api.accountLists.preflight(jsonl, key);
      setPreview(result);
      setSaveKey("");
      setStep(1);
      if (result.summary.invalid > 0) {
        pushToast({ tone: "error", title: "发现需要修复的行", message: `${result.summary.invalid} 行未通过服务端校验，尚未写入账号库。` });
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "服务端校验失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  function editLine(lineNo: number) {
    setStep(0);
    setError("");
    window.setTimeout(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const lines = jsonl.split(/\r?\n/);
      const start = lines.slice(0, Math.max(0, lineNo - 1)).reduce((sum, line) => sum + line.length + 1, 0);
      const end = start + (lines[lineNo - 1]?.length || 0);
      textarea.focus();
      textarea.setSelectionRange(start, end);
      const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight) || 20;
      textarea.scrollTop = Math.max(0, (lineNo - 2) * lineHeight);
    }, 0);
  }

  async function saveList() {
    if (!preview || preview.summary.invalid > 0) return;
    if (!listName.trim()) {
      setError("请输入批次名称，方便后续选择和追踪。");
      return;
    }
    setBusy(true);
    setError("");
    const key = saveKey || createIdempotencyKey("account-list-create");
    if (!saveKey) setSaveKey(key);
    try {
      const result = await api.accountLists.createFromPreview(
        preview,
        listName.trim(),
        jsonl,
        key,
      );
      setCreated(result);
      pushToast({ tone: "success", title: "账号批次已保存", message: `服务端确认写入 ${result.created} 个账号。` });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "账号批次保存失败，请重试。");
    } finally {
      setBusy(false);
    }
  }

  function finish(startTask: boolean) {
    if (!created) return;
    const result = created;
    reset();
    onClose();
    onCreated(result, startTask);
  }

  return (
    <>
      <Modal
        open={open && !discardOpen}
        wide
        busy={busy}
        eyebrow="ACCOUNT INGEST"
        title="导入账号批次"
        description="内容先在服务端逐行校验，通过后才会写入账号库。"
        onClose={requestClose}
      >
        <ol className="flow-stepper" aria-label="导入进度">
          {steps.map((label, index) => (
            <li key={label} className={index < step || created ? "is-done" : index === step ? "is-current" : ""} aria-current={index === step ? "step" : undefined}>
              <span>{index < step || created ? <Icon name="check" size={15} /> : index + 1}</span><b>{label}</b>
            </li>
          ))}
        </ol>

        {step === 0 ? (
          <div className="import-source flow-panel">
            <div
              className={`import-dropzone${dragging ? " is-dragging" : ""}`}
              onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }}
              onDrop={onDrop}
            >
              <span><Icon name="accounts" size={24} /></span>
              <div><b>{sourceName || "拖放 JSONL 到这里"}</b><small>支持 .jsonl、.ndjson、.json 和 .txt，也可以直接粘贴</small></div>
              <button type="button" className="button button--quiet" onClick={() => fileRef.current?.click()}>选择文件</button>
              <input ref={fileRef} type="file" hidden accept=".jsonl,.ndjson,.json,.txt,application/json,application/x-ndjson,text/plain" onChange={onFileChange} />
            </div>
            <label className="jsonl-editor">
              <span><b>JSONL 内容</b><small>一行一个账号对象，内容不会保存在浏览器本地。</small></span>
              <textarea
                ref={textareaRef}
                data-autofocus
                value={jsonl}
                onChange={(event) => { setJsonl(event.target.value); setPreview(null); setSourceName(""); setError(""); setPreflightKey(""); setSaveKey(""); }}
                placeholder={'{"email":"name@example.com","password":"replace-with-password","mailapi_url":"https://mail.example.test/message"}'}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
              <span className="jsonl-editor__meta">
                <small>{inspection.nonEmpty} 条非空记录 · {inspection.bytes.toLocaleString("zh-CN")} 字节</small>
                <small className={inspection.invalid ? "is-danger" : ""}>{inspection.invalid ? `本地发现 ${inspection.invalid} 行 JSON 结构异常` : inspection.nonEmpty ? "本地 JSON 结构检查通过" : "等待输入"}</small>
              </span>
            </label>
          </div>
        ) : null}

        {step === 1 && preview ? (
          <div className="flow-panel">
            <div className={`validation-summary${preview.summary.invalid ? " has-errors" : " is-ready"}`}>
              <span><b>{preview.summary.total}</b>总行数</span>
              <span><b>{preview.summary.valid}</b>可导入</span>
              <span><b>{preview.summary.invalid}</b>需修复</span>
              <span><b>{preview.summary.duplicates || 0}</b>重复提示</span>
              <p>{preview.summary.invalid ? "数据尚未写入。修复所有错误并重新校验后才能继续。" : "服务端预检已通过，可以为这批账号命名。"}</p>
            </div>
            <div className="import-preview-list" aria-label="逐行校验结果">
              {preview.items.map((item) => <PreviewRow key={`${item.line_no}-${item.email}`} item={item} onFix={editLine} />)}
            </div>
            {preview.expires_at ? <p className="preview-expiry">本次预检有效至 {formatDate(preview.expires_at)}，过期后需重新校验。</p> : null}
          </div>
        ) : null}

        {step === 2 && preview ? (
          <div className="flow-panel save-batch-panel">
            {created ? (
              <div className="flow-success" role="status">
                <span><Icon name="check" size={28} /></span>
                <h3>{created.list.name} 已进入账号库</h3>
                <p>服务端确认写入 {created.created} 个账号{created.invalid.length ? `，另有 ${created.invalid.length} 行未写入` : "，没有跳过记录"}。</p>
                <div><button type="button" className="button button--quiet" onClick={() => finish(false)}>完成</button><button type="button" className="button button--primary" onClick={() => finish(true)}>用这批账号创建任务 <Icon name="arrow" size={17} /></button></div>
              </div>
            ) : (
              <>
                <div className="batch-receipt">
                  <span className="batch-receipt__mark"><Icon name="accounts" size={27} /></span>
                  <div><span className="eyebrow">READY TO STORE</span><h3>{preview.summary.valid} 个账号已通过校验</h3><p>保存后会生成新的服务端账号批次，不读取或迁移旧浏览器数据。</p></div>
                </div>
                <label className="flow-field"><span>批次名称</span><input data-autofocus value={listName} maxLength={120} onChange={(event) => { setListName(event.target.value); setError(""); setSaveKey(""); }} placeholder="例如：7 月待重授权" /></label>
                <div className="inline-notice"><Icon name="alert" size={18} /><span>点击保存才会写入服务端；如果请求失败，本页会保留内容供你重试。</span></div>
              </>
            )}
          </div>
        ) : null}

        {isPreviewMode ? <div className="flow-error" role="status">当前是只读预览环境，不能提交导入。</div> : error ? <div className="flow-error" role="alert">{error}</div> : null}

        {!created ? (
          <footer className="flow-actions">
            <button type="button" className="button button--quiet" onClick={step === 0 ? requestClose : () => { setStep((value) => Math.max(0, value - 1)); setError(""); }} disabled={busy}>{step === 0 ? "取消" : "上一步"}</button>
            {step === 0 ? <button type="button" className="button button--primary" onClick={() => void runPreflight()} disabled={busy || !jsonl.trim() || isPreviewMode}>{busy ? <span className="button-spinner" /> : null}{busy ? "校验中" : "交给服务端校验"}</button> : null}
            {step === 1 ? <button type="button" className="button button--primary" onClick={() => { setStep(2); setError(""); }} disabled={preview?.summary.invalid !== 0}>下一步：命名批次 <Icon name="arrow" size={17} /></button> : null}
            {step === 2 ? <button type="button" className="button button--primary" onClick={() => void saveList()} disabled={busy || !listName.trim()}>{busy ? <span className="button-spinner" /> : null}{busy ? "保存中" : "保存到账号库"}</button> : null}
          </footer>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={open && discardOpen}
        title="丢弃当前导入内容？"
        description="尚未保存的 JSONL、校验结果和批次名称会从当前页面清除。"
        confirmLabel="丢弃草稿"
        danger
        onCancel={() => setDiscardOpen(false)}
        onConfirm={closeNow}
      />
    </>
  );
}
